import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Button, CheckBox, CheckBoxGroup, FormField, Grid, List, Pagination, Select, Tab, Tabs, Text, TextArea, TextInput } from "grommet";
import { debounce, uniq } from "lodash";
import * as React from "react";
import Avatar from "react-avatar";
import { useNavigate } from "react-router-dom";
import { useUser } from "../account/AccountStore";
import CustomButton from "../components/CustomButton";
import SkeletonWrapper from "../components/SkeletonWrapper";
import OrganizationSearch from "../organizations/OrganizationSearch";
import { Organization } from "../organizations/OrganizationStore";
import AliasDetails from "./AliasDetails";
import { AliasViewModel, createAlias, createAliasAndUser, CreateAliasRequest, createIdentityAlias, CreateIdentityAliasRequest, GetIdentityOrganizationMembersResponse, IdentityUser, searchUsers, searchUsersExt } from "./AliasStore";

export const MembershipTypeToString = (membershipType: number) => {
    if (membershipType == 0) return "Staff";
    if (membershipType == 1) return "Student";
    if (membershipType == 2) return "Parent";
    if (membershipType == 3) return "Guest";
    return "Unknown";
};

const magnifyingGlass = <FontAwesomeIcon icon={["fad", "magnifying-glass"]}></FontAwesomeIcon>;

const AliasName = (user: IdentityUser) => {
    if (user.firstName || user.lastName) {
        return `${user.firstName || ""} ${user.lastName || ""}`.trim();
    } else if (user.fullName) {
        return user.fullName;
    } else if (user.email) {
        return user.email;
    } else {
        return user.id;
    }
};

const MemberRow = ({ member }: { member: IdentityUser }) => {
    return (
        <Box background={"background"} pad="medium" direction="row" gap="small">
            <Box justify="center">
                <img src={member.photo} width="35px" alt="" />
            </Box>
            <Box justify="between" direction="row" width="100%">
                <Box justify="center">
                    <Text> {AliasName(member)}</Text>
                </Box>
            </Box>
        </Box>
    );
};

const ListSearchResults = ({ label, searchResults, select }: { label: string; searchResults: GetIdentityOrganizationMembersResponse; select: any }) => {
    return (
        <>
            <Box margin={{ top: "small", bottom: "small" }} pad="small" background="background" round="5px" direction="row" justify="between" align="center">
                <Text size="large">{label}</Text>
                <Text size="small">
                    Showing {searchResults.members.length} of {searchResults.total}
                </Text>
            </Box>
            <Box direction="row" gap="small">
                {searchResults.members.map((member) => (
                    <CustomButton
                        size="small"
                        primary
                        key={member.id}
                        onClick={() => select(member)}
                        label={
                            <>
                                <Avatar name={AliasName(member)} src={member.photo} round size="25px"></Avatar> {AliasName(member)}
                            </>
                        }
                    ></CustomButton>
                ))}
            </Box>
        </>
    );
};

const CheckBoxSearchResults = ({ label, value, searchResults, select }) => {
    const [selections, setSelections] = React.useState(value);

    React.useEffect(() => {
        select(selections);
    }, [selections]);

    const onChange = (member) => (e) => {
        if (e.target.checked) {
            setSelections([...selections, member]);
        } else {
            setSelections(selections.filter((a) => a.id !== member.id));
        }
    };

    return (
        <>
            <Box margin={{ top: "small", bottom: "small" }} pad="small" background="background" round="5px" direction="row" justify="between" align="center">
                <Text size="large">{label}</Text>
                <Text size="small">
                    Showing {searchResults.members.length} of {searchResults.total}
                </Text>
            </Box>
            <Box gap="small" pad={{ left: "small" }}>
                {searchResults.members.map((member) => (
                    <CheckBox
                        checked={selections.find((a) => a.id === member.id) != null}
                        key={member.id}
                        label={
                            <Text size="small" key={member.id}>
                                <Avatar name={AliasName(member)} src={member.photo} round size="25px"></Avatar> {AliasName(member)}
                            </Text>
                        }
                        onChange={onChange(member)}
                    ></CheckBox>
                ))}
            </Box>
        </>
    );
};

const CreateAlias = () => {
    const user = useUser();

    const TABS = {
        search: 0,
        new: 1,
        bulk: 2,
        confirm: 3,
    };

    let [organization, setOrganization] = React.useState({} as Organization);

    React.useEffect(() => {
        if (user) {
            setOrganization(user.organization);
        }
    }, [user]);
    const [loading, setLoading] = React.useState(false);

    let [isCreating, setIsCreating] = React.useState(false);

    let [error, setError] = React.useState("");

    const navigate = useNavigate();

    let [staffUserSearchResult, setStaffUserSearchResult] = React.useState({ members: [], total: 0 } as GetIdentityOrganizationMembersResponse);
    let [studentUserSearchResult, setStudentUserSearchResult] = React.useState({ members: [], total: 0 } as GetIdentityOrganizationMembersResponse);
    let [guestUserSearchResult, setGuestUserSearchResult] = React.useState({ members: [], total: 0 } as GetIdentityOrganizationMembersResponse);

    const [index, setIndex] = React.useState(0);
    const onActive = (nextIndex: number) => {
        if (nextIndex != TABS.confirm || alias.identityUserID) {
            setIndex(nextIndex);
        }
        if (nextIndex == TABS.bulk) {
            setAlias({} as CreateAliasRequest);
            setBulkStaffUser([] as IdentityUser[]);
            setBulkStudentsUser([] as IdentityUser[]);
            setBulkGuestsUser([] as IdentityUser[]);
            setBulkCreate(true);
        }

        if (nextIndex == TABS.new) {
            setAlias({
                organizationID: organization.id,
            } as CreateAliasRequest);
        }
    };

    let [alias, setAlias] = React.useState({} as CreateAliasRequest);

    let create = () => {
        setIsCreating(true);
        createAlias(alias as CreateAliasRequest).then(
            (result) => {
                if (result.success) {
                    navigate(`/aliases/${result.aliasID}`);
                } else {
                    setError(result.errors.join("\n"));
                    setIsCreating(false);
                }
            },
            (error) => {
                setError(error);
                setIsCreating(false);
            }
        );
    };

    const createUser = () => {
        setIsCreating(true);
        createAliasAndUser(alias as CreateAliasRequest).then(
            (result) => {
                if (result.success) {
                    navigate(`/aliases/${result.aliasID}`);
                } else {
                    setError(result.errors.join("\n"));
                    setIsCreating(false);
                }
            },
            (error) => {
                setError(error);
                setIsCreating(false);
            }
        );
    };

    const search = (organizationId, filter) => {
        setLoading(true);
        if (organizationId) {
            searchUsers(true, filter, organizationId, 0, 10).then((response) => {
                setStaffUserSearchResult(response[0]);
                setStudentUserSearchResult(response[1]);
                setGuestUserSearchResult(response[2]);
                setLoading(false);
            });
        }
    };

    const debounceSearch = React.useMemo(() => debounce(search, 300), []);

    let [filter, setFilter] = React.useState("");

    React.useEffect(() => {
        debounceSearch(organization.id, filter);
    }, [filter]);

    React.useEffect(() => {
        setStaffUserSearchResult({ members: [], total: 0 } as GetIdentityOrganizationMembersResponse);
        setStudentUserSearchResult({ members: [], total: 0 } as GetIdentityOrganizationMembersResponse);
        setGuestUserSearchResult({ members: [], total: 0 } as GetIdentityOrganizationMembersResponse);
        if (organization.id) {
            search(organization.id, filter);
        }
    }, [organization.id]);

    const select = (user) => {
        setBulkCreate(false);
        setIndex(TABS.confirm);
        setAlias({
            name: `${user.firstName || ""} ${user.lastName || ""}`.trim(),
            photoUri: user.photo,
            email: user.email,
            identityUserID: user.id,
            organizationID: organization.id,
        } as CreateAliasRequest);
    };

    const onChange = (updatedAlias) => {
        setAlias({ ...alias, ...updatedAlias });
    };

    const [bulkCreate, setBulkCreate] = React.useState(false);

    const [bulkStaffUser, setBulkStaffUser] = React.useState([] as IdentityUser[]);
    const [bulkStudentsUser, setBulkStudentsUser] = React.useState([] as IdentityUser[]);
    const [bulkGuestsUser, setBulkGuestsUser] = React.useState([] as IdentityUser[]);

    const bulkSelectStaff = (selections) => {
        setBulkStaffUser(selections);
    };

    const bulkSelectStudents = (selections) => {
        setBulkStudentsUser(selections);
    };

    const bulkSelectGuests = (selections) => {
        setBulkGuestsUser(selections);
    };

    const continueBulkCreate = () => {
        setIndex(TABS.confirm);
    };

    const confirmBulkCreate = () => {
        let promises = uniq([...bulkStaffUser, ...bulkStudentsUser, ...bulkGuestsUser].map((u) => u.id)).map((userID) => createIdentityAlias({ userID, organizationID: organization.id } as CreateIdentityAliasRequest));

        setIsCreating(true);
        Promise.all(promises)
            .then((results) => {
                if (results.every((result) => result.success)) {
                    navigate(`/aliases`);
                } else {
                    setError(
                        results
                            .filter((result) => !result.success)
                            .map((result) => result.errors.join("\n"))
                            .join("\n")
                    );
                }
                setIsCreating(false);
            })
            .catch(() => {
                setIsCreating(false);
            });
    };

    return (
        <div>
            <h1>Create Alias</h1>
            <Box>
                <Box direction="row" gap="small" justify="center">
                    <CustomButton primary={index == 0} onClick={() => onActive(0)} label="Search Users"></CustomButton>
                    <CustomButton primary={index == 1} onClick={() => onActive(1)} label="New User"></CustomButton>
                    <CustomButton primary={index == 2} onClick={() => onActive(2)} label="Add Bulk"></CustomButton>
                </Box>
                <Tabs activeIndex={index} onActive={onActive}>
                    <Tab>
                        {organization.id && (
                            <Box direction="row" pad="small" justify="end">
                                <TextInput placeholder="Search Users ..." icon={magnifyingGlass} value={filter} onChange={(e) => setFilter(e.target.value)} disabled={isCreating} />
                            </Box>
                        )}

                        <SkeletonWrapper loading={loading}>
                            {staffUserSearchResult.total > 0 && <ListSearchResults label="Staff" searchResults={staffUserSearchResult} select={select}></ListSearchResults>}
                            {studentUserSearchResult.total > 0 && <ListSearchResults label="Students" searchResults={studentUserSearchResult} select={select}></ListSearchResults>}
                            {guestUserSearchResult.total > 0 && <ListSearchResults label="Guests" searchResults={guestUserSearchResult} select={select}></ListSearchResults>}
                        </SkeletonWrapper>
                    </Tab>
                    <Tab>
                        <AliasDetails alias={alias} isBusy={isCreating} onChange={onChange}></AliasDetails>
                        <Button primary onClick={createUser} label="Create"></Button>
                    </Tab>
                    <Tab>
                        {organization.id && (
                            <Box pad="small">
                                <TextInput placeholder="Search Users ..." icon={magnifyingGlass} value={filter} onChange={(e) => setFilter(e.target.value)} disabled={isCreating} />
                            </Box>
                        )}

                        {bulkCreate && (
                            <>
                                {staffUserSearchResult.total > 0 && <CheckBoxSearchResults label="Staff" value={bulkStaffUser} searchResults={staffUserSearchResult} select={bulkSelectStaff}></CheckBoxSearchResults>}
                                {studentUserSearchResult.total > 0 && <CheckBoxSearchResults label="Students" value={bulkStudentsUser} searchResults={studentUserSearchResult} select={bulkSelectStudents}></CheckBoxSearchResults>}
                                {guestUserSearchResult.total > 0 && <CheckBoxSearchResults label="Guests" value={bulkGuestsUser} searchResults={guestUserSearchResult} select={bulkSelectGuests}></CheckBoxSearchResults>}
                            </>
                        )}

                        <br />
                        <Button primary onClick={continueBulkCreate} label="Continue"></Button>
                    </Tab>
                    <Tab>
                        {bulkCreate && (
                            <>
                                <Text>Create the following aliases</Text>
                                <Box gap="small">
                                    {bulkStaffUser.map((member, i) => (
                                        <MemberRow key={"bulkStaff" + member.id} member={member}></MemberRow>
                                    ))}
                                    {bulkStudentsUser.map((member, i) => (
                                        <MemberRow key={"bulkStudent" + member.id} member={member}></MemberRow>
                                    ))}
                                    {bulkGuestsUser.map((member, i) => (
                                        <MemberRow key={"bulkGuest" + member.id} member={member}></MemberRow>
                                    ))}
                                </Box>
                            </>
                        )}
                        {!bulkCreate && <AliasDetails alias={alias} isBusy={isCreating} onChange={onChange}></AliasDetails>}
                        <br />
                        {bulkCreate && <Button primary onClick={confirmBulkCreate} label="Confirm Bulk"></Button>}
                        {!bulkCreate && <Button primary onClick={create} disabled={isCreating || !alias.name} label="Create"></Button>}
                    </Tab>
                    {error && (
                        <Tab title="Error">
                            <Box>{error && <pre>{JSON.stringify(error, null, 2)}</pre>}</Box>
                        </Tab>
                    )}
                </Tabs>
            </Box>
        </div>
    );
};

export default CreateAlias;
